import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { FaPlus, FaTrashAlt, FaChevronDown } from "react-icons/fa";
import Select from "react-select";
import axiosConfig from "../../Service/AxiosConfig";
import { PopupModal } from "../PopupModal/PopupModal";
import { useDispatch, useSelector } from "react-redux";
import { updateTempForm } from "../../redux/BookingFormSlice";
import "./ServiceDetails.css";
import ShowDeleteModal from "../PopupModal/ShowDeleteModal";
const EMPTY_SERVICE = {
  primary_service: "",
  service_type: "",
  expected_start_date: "",
  estimated_duration_days: "",
  language: [],
  service_gender: "",
};

const fieldsToCheck = [
  "primary_service",
  "service_type",
  "expected_start_date",
  "estimated_duration_days",
  "language",
  "service_gender",
];

const ServiceDetails = ({ form, setForm, isLocked }) => {
  const dispatch = useDispatch();
  const tempData = useSelector((s) => s.bookingForm.tempData);
  const [editIndex, setEditIndex] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serviceList, setServiceList] = useState([]);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [serviceTypeList, setServiceTypeList] = useState([]);
  const [language, setLanguage] = useState([]);
  const [extraServices, setExtraServices] = useState([]);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [popupType, setPopupType] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const canAddAlternate = !!form?.service_id && editIndex === null && !saving;

  const [serviceError, setServiceError] = useState({
    main: "",
    extra: {},
  });

  const hasFetchedRef = useRef(false);
  const serviceOptions = serviceList.map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const serviceTypeOptions = serviceTypeList.map((s) => ({
    value: s.id,
    label: s.title,
  }));

  const languageOptions = language.map((l) => ({
    value: l.id,
    label: l.language,
  }));

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];
  const isEmpty = (v) => v === null || v === undefined || v === "";
  const normalizeNumber = (v) =>
    v === "" || v === null || v === undefined ? null : Number(v);

  useEffect(() => {
    axiosConfig
      .get("/catlog/services/?is_suspended=false")
      .then((r) => setServiceList(r.data.results || []));
    axiosConfig
      .get("/masters/service-types/?is_suspended=false")
      .then((r) => setServiceTypeList(r.data.results || []));
    axiosConfig
      .get("/masters/languages/?is_suspended=false")
      .then((r) => setLanguage(r.data.results || []));
  }, []);

  const fetchLeadServices = useCallback(async () => {
    if (!form?.lead_id || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const res = await axiosConfig.get(
      `/accounts/leadservices/?lead=${form.lead_id}`,
    );

    const [main, ...extra] = res.data.results || [];
    if (!main) return;

    setForm((p) => ({
      ...p,
      service_id: main.id,
      primary_service: main.service || "",
      service_type: main.service_type?.id || null,
      expected_start_date: main.expected_start_date || "",
      estimated_duration_days: main.estimated_duration_days,
      language: main.language?.map((l) => l.id ?? l) || [],
      service_gender: main.service_gender || "",
    }));

    setExtraServices(
      extra.map((s) => ({
        service_id: s.id,
        primary_service: s.service || "",
        service_type: s.service_type?.id || "",
        expected_start_date: s.expected_start_date || null,
        estimated_duration_days: s.estimated_duration_days,
        language: s.language?.map((l) => l.id ?? l) || [],
        service_gender: s.service_gender || "",
      })),
    );
  }, [form?.lead_id, setForm]);

  useEffect(() => {
    fetchLeadServices();
  }, [fetchLeadServices]);

  const hasAnyData =
    fieldsToCheck.some((k) => form?.[k]?.toString().trim()) ||
    extraServices.some((s) =>
      fieldsToCheck.some((k) => s?.[k]?.toString().trim()),
    );

  useEffect(() => {
    if (!form?.id || isDirty) return;
    setEditIndex(hasAnyData ? null : -1);
  }, [form?.id, hasAnyData, isDirty]);

  const syncRedux = (extra) => {
    dispatch(
      updateTempForm({
        booking_id: form.booking_id,
        data: {
          service_details: {
            main: {
              service_id: form.service_id,
              primary_service: form.primary_service,
              service_type: form.service_type,
              expected_start_date: form.expected_start_date || null,
              estimated_duration_days: normalizeNumber(
                form.estimated_duration_days,
              ),
              language: form.language,
              service_gender: form.service_gender,
            },
            extra,
          },
        },
      }),
    );
  };

  const updateMain = (field, value) => {
    setIsDirty(true);
    setForm((p) => {
      const updated = { ...p, [field]: value };
      syncRedux(extraServices);
      return updated;
    });
  };

  const updateExtra = (index, field, value) => {
    setIsDirty(true);
    const updated = extraServices.map((s, i) =>
      i === index ? { ...s, [field]: value } : s,
    );
    setExtraServices(updated);
    syncRedux(updated);
  };

  const addServiceRow = () => {
    const updated = [...extraServices, { ...EMPTY_SERVICE }];
    setExtraServices(updated);
    setEditIndex(updated.length - 1);
    setIsDirty(true);
    setExpanded(true);
    syncRedux(updated);
  };

  const handleSaveMain = async () => {
    if (saving) return;
    setSaving(true);
    setServiceError({ main: "", extra: {} });
    if (isEmpty(form.primary_service)) {
      setServiceError({
        main: "Please select a service",
        extra: {},
      });
      setSaving(false);
      return;
    }

    const leadId = form.lead?.id || form.lead_id;
    if (!leadId) {
      setSaving(false);
      return;
    }

    const payload = [
      {
        service_id: form?.service_id,
        lead: leadId,
        primary_service: form.primary_service,
        service_type: form.service_type || null,
        expected_start_date: form.expected_start_date || null,
        estimated_duration_days: normalizeNumber(form.estimated_duration_days),
        language: form.language || [],
        service_gender: form.service_gender || null,
      },
    ];

    try {
      await axiosConfig.patch("/accounts/leadservices/bulk-update/", payload, {
        headers: { "Content-Type": "application/json" },
      });

      setPopupType("success");
      setMessage("Service RequestSaved Successfully");

      setEditIndex(null);
      setExpanded(false);
      setIsDirty(false);
      hasFetchedRef.current = false;
      await fetchLeadServices();
    } catch (err) {
      setPopupType("error");
      setError("Failed to save main service");
    } finally {
      setSaving(false);
    }
  };
  const handleSaveExtra = async (index) => {
    if (saving) return;
    setSaving(true);
    setServiceError({ main: "", extra: {} });
    const service = extraServices[index];
    if (isEmpty(service.primary_service)) {
      setServiceError({
        main: "",
        extra: {
          [index]: { primary_service: "Please select a service" },
        },
      });
      setSaving(false);
      return;
    }

    const leadId = form.lead?.id || form.lead_id;
    if (!leadId) {
      setSaving(false);
      return;
    }

    const payload = [
      {
        service_id: service?.service_id,
        lead: leadId,
        primary_service: service.primary_service,
        service_type: service.service_type || null,
        expected_start_date: service.expected_start_date || null,
        estimated_duration_days: normalizeNumber(
          service.estimated_duration_days,
        ),
        language: service.language || [],
        service_gender: service.service_gender || null,
      },
    ];

    try {
      await axiosConfig.patch("/accounts/leadservices/bulk-update/", payload, {
        headers: { "Content-Type": "application/json" },
      });

      setPopupType("success");
      setMessage(" Service Request Saved Successfully");

      setEditIndex(null);
      setIsDirty(false);
      hasFetchedRef.current = false;
      await fetchLeadServices();
    } catch (err) {
      setPopupType("error");
      setError("Failed to save extra service");
    } finally {
      setSaving(false);
    }
  };
const confirmDeleteService = async () => {
  setSaving(true);

  try {
   
    if (!deleteId) {
      setExtraServices((prev) =>
        prev.filter((_, i) => i !== deleteIndex),
      );

      setEditIndex(null);   
      setIsDirty(false);

      setPopupType("success");
      setMessage("Service removed");
      return;
    }

    await axiosConfig.delete(`/accounts/leadservices/${deleteId}/`);

    setExtraServices((prev) =>
      prev.filter((s) => s.service_id !== deleteId),
    );

    setEditIndex(null);   
    setPopupType("success");
    setMessage("Service deleted successfully");
  } catch (err) {
    console.error(err);
    setPopupType("error");
    setError("Failed to delete service");
  } finally {
    setShowDeletePopup(false);
    setDeleteId(null);
    setDeleteIndex(null);
    setSaving(false);
  }
};

  return (
    <>
      <Accordion
        key={expanded ? "open" : "closed"}
        allowZeroExpanded
        preExpanded={expanded ? ["service"] : []}
        onChange={(uuids) => {
          setExpanded(uuids.includes("service"));
        }}
      >
        <AccordionItem uuid="service">
          <AccordionItemHeading>
            <AccordionItemButton className="bd-header">
              <h4 className="bd-title">Service Request</h4>
              <span className="bd-add-btn">
                Add Details
                <FaChevronDown
                  style={{
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                    marginLeft: "5px",
                  }}
                />
              </span>
            </AccordionItemButton>
          </AccordionItemHeading>

          <AccordionItemPanel className="bd-panel">
            <div
              className={`bd-section-grid bd-grid-3 service-cards ${
                isLocked ? "locked-section" : ""
              }`}
            >
              <div className="form-group">
                <label
                  className={`form-label ${editIndex !== -1 ? "view-mode-label" : ""}`}
                >
                  Primary Service
                </label>
                <sup style={{ color: "red" }}>*</sup>
                <Select
                  name="primary_service"
                  options={serviceOptions}
                  value={serviceOptions.find(
                    (o) => o.value === form.primary_service,
                  )}
                  onChange={(s) => updateMain("primary_service", s?.value)}
                  isDisabled={editIndex !== -1 || isLocked}
                  placeholder="Select Service"
                  isSearchable={false}
                  classNamePrefix="height-select"
                  className={`${editIndex !== -1 ? "view-mode-text" : ""}`}
                />
                {serviceError.main && (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    {serviceError.main}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label
                  className={`form-label ${editIndex !== -1 ? "view-mode-label" : ""}`}
                >
                  Service Type
                </label>
                <Select
                  options={serviceTypeOptions}
                  value={serviceTypeOptions.find(
                    (o) => o.value === form.service_type,
                  )}
                  onChange={(s) => updateMain("service_type", s?.value)}
                  isDisabled={editIndex !== -1 || isLocked}
                  placeholder="Select  Service Type"
                  isSearchable={false}
                  classNamePrefix="height-select"
                  className={editIndex !== -1 ? "view-mode-text" : ""}
                />
              </div>

              <div className="form-group">
                <label
                  className={`form-label ${editIndex !== -1 ? "view-mode-label" : ""}`}
                >
                  Expected Start Date
                </label>
                <input
                  type="date"
                  value={form.expected_start_date || ""}
                  onChange={(e) =>
                    updateMain("expected_start_date", e.target.value)
                  }
                  disabled={editIndex !== -1 || isLocked}
                  className={`form-control ${editIndex !== -1 ? "view-mode-text" : ""}`}
                />
              </div>

              <div className="form-group">
                <label
                  className={`form-label ${editIndex !== -1 ? "view-mode-label" : ""}`}
                >
                  Estimated Duration (Days)
                </label>
                <input
                  type="number"
                  placeholder="Estimated Duration Days"
                  value={form.estimated_duration_days}
                  onChange={(e) =>
                    updateMain("estimated_duration_days", e.target.value)
                  }
                  disabled={editIndex !== -1 || isLocked}
                  className={`form-control ${editIndex !== -1 ? "view-mode-text" : ""}`}
                />
              </div>

              <div className="form-group">
                <label
                  className={`form-label ${editIndex !== -1 ? "view-mode-label" : ""}`}
                >
                  Gender
                </label>
                <Select
                  options={genderOptions}
                  value={genderOptions.find(
                    (o) => o.value === form.service_gender,
                  )}
                  onChange={(s) => updateMain("service_gender", s?.value)}
                  isDisabled={editIndex !== -1 || isLocked}
                  placeholder="Select Gender"
                  isSearchable={false}
                  classNamePrefix="height-select"
                  className={editIndex !== -1 ? "view-mode-text" : ""}
                />
              </div>

              <div className="form-group">
                <label
                  className={`form-label ${
                    editIndex !== -1 ? "view-mode-label" : ""
                  }`}
                >
                  Language
                </label>
                <Select
                  isMulti
                  options={languageOptions}
                  value={languageOptions.filter((opt) =>
                    (form.language || []).includes(opt.value),
                  )}
                  onChange={(selected) => {
                    const values = selected ? selected.map((s) => s.value) : [];
                    updateMain("language", values);
                  }}
                  isDisabled={editIndex !== -1 || isLocked}
                  placeholder="Select Language"
                  isSearchable={false}
                  classNamePrefix="height-select"
                  className={editIndex !== -1 ? "view-mode-text" : ""}
                />
              </div>

              <div className="form-group full-width">
                <div className="bd-actions">
                  {editIndex !== -1 ? (
                    <button
                      className="bd-edit-btn"
                      onClick={() => {
                        setEditIndex(-1);
                        setIsDirty(true);
                        setExpanded(true);
                      }}
                    >
                      Edit
                    </button>
                  ) : (
                    hasAnyData &&
                    editIndex === -1 && (
                      <button
                        className="bd-save-btn"
                        onClick={handleSaveMain}
                        disabled={saving}
                      >
                        Save
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
            {extraServices.map((s, i) => (
              <div
                key={i}
                className={`bd-section-grid bd-grid-3 service-cards ${
                  isLocked ? "locked-section" : ""
                }`}
              >
                {editIndex === i && (
                  <button
                    className="delete-service-btn"
                    onClick={() => {
                      setDeleteId(extraServices[i]?.service_id || null);
                      setDeleteIndex(i);
                      setShowDeletePopup(true);
                    }}
                  >
                    <FaTrashAlt size={14} />
                  </button>
                )}

                <div className="form-group">
                  <label
                    className={`form-label ${editIndex !== i ? "view-mode-label" : ""}`}
                  >
                    Primary Service
                  </label>
                  <sup style={{ color: "red" }}>*</sup>
                  <Select
                    options={serviceOptions}
                    value={serviceOptions.find(
                      (o) => o.value === s.primary_service,
                    )}
                    onChange={(x) =>
                      updateExtra(i, "primary_service", x?.value)
                    }
                    isDisabled={editIndex !== i || isLocked}
                    placeholder="Select Service"
                    isSearchable={false}
                    classNamePrefix="height-select"
                    className={editIndex !== i ? "view-mode-text" : ""}
                  />
                  {serviceError.extra?.[i]?.primary_service && (
                    <span style={{ color: "red", fontSize: "12px" }}>
                      {serviceError.extra[i].primary_service}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label
                    className={`form-label ${editIndex !== i ? "view-mode-label" : ""}`}
                  >
                    Service Type
                  </label>
                  <Select
                    options={serviceTypeOptions}
                    value={serviceTypeOptions.find(
                      (o) => o.value === s.service_type,
                    )}
                    onChange={(x) => updateExtra(i, "service_type", x?.value)}
                    isDisabled={editIndex !== i || isLocked}
                    placeholder="Select Service Type"
                    isSearchable={false}
                    classNamePrefix="height-select"
                    className={editIndex !== i ? "view-mode-text" : ""}
                  />
                </div>

                <div className="form-group">
                  <label
                    className={`form-label ${editIndex !== i ? "view-mode-label" : ""}`}
                  >
                    Expected Start Date
                  </label>
                  <input
                    type="date"
                    value={s.expected_start_date || ""}
                    onChange={(e) =>
                      updateExtra(i, "expected_start_date", e.target.value)
                    }
                    disabled={editIndex !== i || isLocked}
                    className={`form-control ${editIndex !== i ? "view-mode-text" : ""}`}
                  />
                </div>

                <div className="form-group">
                  <label
                    className={`form-label ${editIndex !== i ? "view-mode-label" : ""}`}
                  >
                    Estimated Duration (Days)
                  </label>
                  <input
                    type="number"
                    placeholder="Estimated Duration Days"
                    value={s.estimated_duration_days}
                    onChange={(e) =>
                      updateExtra(i, "estimated_duration_days", e.target.value)
                    }
                    disabled={editIndex !== i || isLocked}
                    className={`form-control ${editIndex !== i ? "view-mode-text" : ""}`}
                  />
                </div>

                <div className="form-group">
                  <label
                    className={`form-label ${editIndex !== i ? "view-mode-label" : ""}`}
                  >
                    Gender
                  </label>
                  <Select
                    options={genderOptions}
                    value={genderOptions.find(
                      (o) => o.value === s.service_gender,
                    )}
                    onChange={(x) => updateExtra(i, "service_gender", x?.value)}
                    isDisabled={editIndex !== i || isLocked}
                    placeholder="Select Relation"
                    isSearchable={false}
                    classNamePrefix="height-select"
                    className={editIndex !== i ? "view-mode-text" : ""}
                  />
                </div>
                <div className="form-group">
                  <label
                    className={`form-label ${editIndex !== i ? "view-mode-label" : ""}`}
                  >
                    Language
                  </label>
                  <Select
                    isMulti
                    options={languageOptions}
                    value={languageOptions.filter((opt) =>
                      (s.language || []).includes(opt.value),
                    )}
                    onChange={(selected) => {
                      const values = selected
                        ? selected.map((s) => s.value)
                        : [];
                      updateExtra(i, "language", values);
                    }}
                    isDisabled={editIndex !== i || isLocked}
                    placeholder="Select Language"
                    isSearchable={false}
                    classNamePrefix="height-select"
                    className={editIndex !== i ? "view-mode-text" : ""}
                  />
                </div>

                <div className="form-group full-width">
                  <div className="bd-actions">
                    {editIndex !== i ? (
                      <button
                        className="bd-edit-btn"
                        onClick={() => {
                          setEditIndex(i);
                          setIsDirty(true);
                          setExpanded(true);
                        }}
                      >
                        Edit
                      </button>
                    ) : (
                      hasAnyData &&
                      editIndex === i && (
                        <button
                          className="bd-save-btn"
                          onClick={() => handleSaveExtra(i)}
                          disabled={saving}
                        >
                          Save
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            ))}

            {canAddAlternate && !isLocked && (
              <button className="add-btn" onClick={addServiceRow}>
                <FaPlus /> Add Service
              </button>
            )}
          </AccordionItemPanel>
        </AccordionItem>
      </Accordion>
      {showDeletePopup && (
        <ShowDeleteModal
          open={showDeletePopup}
          onConfirm={confirmDeleteService}
          onCancel={() => setShowDeletePopup(false)}
        />
      )}
      {popupType && (
        <PopupModal
          popupType={popupType}
          setpopupType={setPopupType}
          Message={message}
          error={error}
          setMessage={setMessage}
        />
      )}
    </>
  );
};

export default ServiceDetails;
